import { Link } from 'react-router-dom';
import { Waypoints, BookOpenCheck, PenLine, Shapes } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { AUTH_ENABLED } from '../../config/features';

export function ProductOverviewSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const prefersReducedMotion = useReducedMotion();

  const features = [
    {
      icon: Waypoints,
      title: "Reasoning Partner",
      description: "Explore directions with structured reasoning and verified sources. Evaluate novelty and feasibility early to avoid late pivots."
    },
    {
      icon: BookOpenCheck,
      title: "Smart Literature",
      description: "Retrieve the most relevant papers and extract key insights. Cut time spent on reference management and note filing."
    },
    {
      icon: PenLine,
      title: "Efficient Writing",
      description: "Strengthen structure and academic tone with smart guidance. Adapt to journal rules automatically and lift language barriers."
    },
    {
      icon: Shapes,
      title: "Cross-Field Collaboration",
      description: "Reveal high value interdisciplinary intersections. Plan protocols and publishable outputs that raise impact and significance."
    }
  ];

  return (
    <section ref={ref} className="relative py-20 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.08),transparent_70%)]"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[--text-primary] mb-4">
            Think Better, Write Faster, with Scholar42
          </h2>
          <p className="text-lg sm:text-xl text-[--text-secondary] max-w-3xl mx-auto">
            A true collaborator that explores direction, tracks reasoning, verifies sources, and drafts to journal standards.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                /* 仅缩小卡片：padding 从 p-8 → p-6 */
                className="group relative bg-white/70 dark:bg-slate-800/70 backdrop-blur-md p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700 hover:border-primary-400 dark:hover:border-primary-600"
              >
                {/* 缩小装饰角：w-32 h-32 → w-24 h-24 */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary-100/40 dark:from-primary-900/20 to-transparent rounded-bl-full"></div>

                <div className="relative">
                  {/* 图标容器从 p-4 → p-3，图标从 w-8 h-8 → w-6 h-6 */}
                  <div className="p-3 bg-gradient-to-br from-primary-500 to-blue-600 dark:from-primary-600 dark:to-blue-700 rounded-2xl mb-3 inline-block shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-300">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {/* 标题从 text-xl → text-lg，间距略缩 */}
                  <h3 className="text-lg font-bold text-[--text-primary] mb-2">{feature.title}</h3>
                  {/* 正文从默认 → text-sm，保持行距 */}
                  <p className="text-sm text-[--text-secondary] leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="mt-16 text-center"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {AUTH_ENABLED ? (
            <Link
              to="/login"
              className="inline-block px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Get Started with Scholar42
            </Link>
          ) : (
            <span className="inline-block px-8 py-4 text-lg font-semibold text-slate-400 dark:text-slate-500 bg-slate-200 dark:bg-slate-700/60 rounded-xl shadow-sm cursor-not-allowed select-none">
              Coming Soon
            </span>
          )}
        </motion.div>
      </div>
    </section>
  );
}
